import React from "react";
import type { ExpandedStore } from "./useExpandedStore.js";

export function useEnrichExpandedStoreWithParentPathIds<
  I extends { id: string; pathForPdf: string },
>(
  expandedStore: ExpandedStore,
  items: I[],
  selectedItemId: string | null,
  ready: boolean
) {
  // Latched per item, not once per mount: a view that moves the selection
  // itself — the wizard stepping to the next question — selects items the user
  // never clicked, and those can sit in a collapsed subtree. Latching on the id
  // still leaves a manual collapse of the current item's path alone.
  const enrichedFor = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!ready || !selectedItemId) return;
    if (enrichedFor.current === selectedItemId) return;

    // The document query returns children of expanded items only, so a
    // selection can name an item that has not arrived yet. Expanding it asks
    // the query for it; the latch stays open until it arrives.
    const item = items.find(i => i.id === selectedItemId);
    if (!item) {
      if (!expandedStore.isItemExpanded(selectedItemId)) {
        expandedStore.setItemExpanded(selectedItemId, true);
      }
      return;
    }

    enrichedFor.current = selectedItemId;

    // `pathForPdf` ends in a separator ("1.1." under "1."), so a plain prefix
    // test cannot take item 10 for a descendant of item 1. The root (empty
    // path) is left out: expanding it changes nothing visible, but the extra
    // expansion refetches the document and remounts every create input.
    const itemParentPathIds = items
      .filter(
        i =>
          i.id !== item.id &&
          i.pathForPdf !== "" &&
          item.pathForPdf.startsWith(i.pathForPdf)
      )
      .map(i => i.id);

    if (itemParentPathIds.some(id => !expandedStore.isItemExpanded(id))) {
      expandedStore.setItemsExpanded(itemParentPathIds, true);
    }
  }, [expandedStore, items, selectedItemId, ready]);
}
